import { InvoicePaymentStatus } from '../models/invoice-payment-status';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { CustomTable } from '../components/CustomTable';
import ErrorBoundary from '../components/ErrorBoundary';
import { NoDataComponent } from '../components/NoDataComponent';
import { PaginationComponent } from '../components/Pagination';
import {
    EditInvoice,
    EditProps,
    Invoice,
    InvoiceTableInnerProps,
    TableInvoiceComponentProps,
} from '../models/table-models';
import {
    EmailRecipientItem,
    emailInvoice,
    emailInvoices,
    getInvoiceEmailRecipient,
    resendInvoiceEmail,
    markInvoiceAsPaid,
    deleteInvoices, useEditForSaleInvoice,
    useEditInvoices,
    useGetInvoices,
    useGetSales,
} from '../utils/clients-hooks';
import { useInvoiceData } from '../utils/data-hooks';
import { invoicesTableHeaders, sentInvoicesTableHeaders } from '../utils/Enums/TableHeaders';
import { AddEditInvoice } from './AddEditInvoice';
import { ErrorComponent } from '../components/ErrorComponent';
import { InvoiceView } from './InvoiceView';
import { resourceCache } from 'use-async-resource';
import { transformDateToString, transformPriceToString, getCurrencySymbol } from '../utils/helper-functions';
import { AddMonthlyInvoices } from './AddMonthlyInvoices';
import { Role } from '../utils/Enums/Roles';
import { AddActionComponent } from '../components/AddActionComponent';
import { fetchTasksAll } from '../utils/dashboard-hooks';
import { CustomToastContent } from '../components/CustomToastContent';
import { toast } from 'react-toastify';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';
import { showBackendError } from '../utils/backend-error';
import { CustomButton } from '../components/CustomButton';
import { EditDocument } from "../components/AddInvoicesToDocument";
import dateFormat from "dateformat";
import { date } from "../utils/Enums/Common";
import { ExportInvoices } from "../exports/ExportInvoices";
import { ResendEmailModal } from '../components/ResendEmailModal';
import { BranchActionComponent } from '../components/BranchActionComponent';
import { PaymentMethodIcon } from '../components/PaymentMethodIcon';
import OpeningBalances from './OpeningBalances';
import { invoiceRowClassName, paymentStatusLabel } from '../utils/invoiceRowStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faLayerGroup } from '@fortawesome/free-solid-svg-icons';

/** Най-ранната и най-късната дата в група редове - периодът на обединената фактура. */
const earliest = (invoices: Invoice[], field: 'fromDate' | 'duePaymentDate') =>
    invoices.map((invoice) => invoice[field]).reduce((a, b) => (new Date(a) < new Date(b) ? a : b));
const latest = (invoices: Invoice[], field: 'fromDate' | 'duePaymentDate') =>
    invoices.map((invoice) => invoice[field]).reduce((a, b) => (new Date(a) > new Date(b) ? a : b));

/**
 * Колоните с подредба. Имената са същите като в InvoicingFilter.SORTABLE_COLUMNS на бекенда - натисне ли
 * се заглавие, което той не разпознава, подредбата просто се игнорира и остава подразбиращата се.
 */
const SORTABLE_COLUMNS = ['invoiceNumber', 'creationDate', 'paymentProcessingStatus'];

export const Invoices = ({ filter, pagination, refetchTurnovers }: TableInvoiceComponentProps & {
    refetchTurnovers: () => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [isLoading, setIsLoading] = useState(false);
    const [activeSubTab, setActiveSubTab] = useState<'obligations' | 'sent'>('obligations');

    const [selectedItem, setSelectedItem] = useState<Invoice>();

    // Подредбата е част от филтъра, защото оттам минава към заявката. Подразбирането остава серверното -
    // id низходящо - докато някой не натисне заглавие.
    const [sortBy, setSortBy] = useState<string>();
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');

    const activeFilter = {
        ...filter,
        sentByMail: activeSubTab === 'sent',
        includeOpeningBalances: activeSubTab === 'obligations',
        sortBy,
        sortDirection: sortBy ? sortDirection : undefined,
    };

    const handleSortChange = (column: string) => {
        pagination.setCurrentPage(1);
        resourceCache(useGetInvoices).clear();
        if (sortBy === column) {
            setSortDirection((current) => (current === 'ASC' ? 'DESC' : 'ASC'));
            return;
        }
        setSortBy(column);
        setSortDirection('DESC');
    };

    const [getInvoices, fetchNewInvoices] = useInvoiceData<Invoice[]>(useGetInvoices, pagination, activeFilter);

    const refetchEntitiesHook = async () => {
        setIsLoading(true);
        resourceCache(useGetInvoices).clear();
        await fetchNewInvoices({
            'pager.page': pagination.currentPage,
            'pager.itemsPerPage': pagination.pageSize,
            pagination,
            filter: activeFilter,
        });
        refetchTurnovers();
        setIsLoading(false);
    };

    const handleSubTabChange = (tab: 'obligations' | 'sent') => {
        if (tab === activeSubTab) return;
        pagination.setCurrentPage(1);
        resourceCache(useGetInvoices).clear();
        setActiveSubTab(tab);
    };


    useEffect(() => {
        if (selectedItem !== undefined) {
            // И документният тип, не само id-то: сторнираната фактура дава два реда с едно и също id -
            // самата фактура и кредитното известие. Търсенето само по id връщаше първия, тоест избереш
            // ли известието, изборът веднага се подменяше с фактурата и прегледът показваше нея.
            setSelectedItem(() => getInvoices().paidInvoices.find(
                ({ id, documentType }) => id === selectedItem.id && documentType === selectedItem.documentType));
        }
    }, [getInvoices().paidInvoices]);

    return (
        <>
            {selectedItem?.openingBalanceId
                ? <OpeningBalances balanceId={selectedItem.openingBalanceId} onClose={() => setSelectedItem(undefined)} onChanged={refetchEntitiesHook} />
                : <InvoiceView title={t('Details for') + " " + t(selectedItem?.documentType + "")} item={selectedItem}
                    setSelectedItem={setSelectedItem} refetchEntitiesHook={refetchEntitiesHook} />}
            <div className='d-flex w-100 flex-row justify-content-between mb-3'>
                <div className={"w-25"}>
                    {
                        isLoading ? <Spinner animation='border' />
                            :
                            activeSubTab === 'obligations' &&
                            <AddActionComponent
                                title={t('addPayment')}
                                addComponent={AddMonthlyInvoices}
                                size='xl'
                                accessActionRoles={[Role.FINANCIAL, Role.ADMIN, Role.COORDINATOR]}
                                {...{ refetchEntitiesHook }}
                                isLoading={isLoading}
                            />
                    }
                </div>
            </div>
            <div className="d-flex border-bottom justify-content-center mb-3 p-3 px-1 w-100">
                <div className="d-flex w-100 justify-content-between">
                    <div
                        role="button"
                        className={`d-flex justify-content-center w-50 step-nav-link text-center ${activeSubTab === 'obligations' ? 'active' : ''}`}
                        onClick={() => handleSubTabChange('obligations')}
                    >
                        <h5>{t('allDuties')}</h5>
                    </div>
                    <div
                        role="button"
                        className={`d-flex justify-content-center w-50 step-nav-link text-center ${activeSubTab === 'sent' ? 'active' : ''}`}
                        onClick={() => handleSubTabChange('sent')}
                    >
                        <h5>{t('sentEmails')}</h5>
                    </div>
                </div>
            </div>
            <ErrorBoundary fallback={<ErrorComponent errorText={t('invoices')} />}>
                <Suspense fallback={<Spinner animation='border' />}>
                    <InvoicesInner
                        filter={activeFilter}
                        pagination={pagination}
                        getEntities={getInvoices}
                        setSelectedItem={setSelectedItem}
                        activeSubTab={activeSubTab}
                        sortBy={sortBy}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        {...{ refetchEntitiesHook }}
                    />
                </Suspense>
            </ErrorBoundary>
        </>
    );
};

const InvoicesInner = ({
    filter,
    getEntities,
    pagination,
    refetchEntitiesHook,
    setSelectedItem,
    activeSubTab,
    sortBy,
    sortDirection,
    onSortChange,
}: InvoiceTableInnerProps & {
    activeSubTab: 'obligations' | 'sent';
    sortBy?: string;
    sortDirection?: 'ASC' | 'DESC';
    onSortChange: (column: string) => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoice' });
    const { t: commonT } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: openingT } = useTranslation('translation', { keyPrefix: 'openingBalances' });
    const { paidInvoices, pager } = getEntities();
    const [idOfEmailLoadingInvoice, setIdOfEmailLoadingInvoice] = useState<number>();
    const [multiMailLoading, setMultiMailLoading] = useState<boolean>(false);
    const [selectedItems, setSelectedItems] = useState<Invoice[]>([]);
    const [emailTarget, setEmailTarget] = useState<Invoice>();
    const [emailRecipients, setEmailRecipients] = useState<(string | EmailRecipientItem)[]>([]);
    const [emailRecipientStr, setEmailRecipientStr] = useState<string>();
    const [emailModalLoading, setEmailModalLoading] = useState<boolean>(false);
    const [loadingRecipient, setLoadingRecipient] = useState<boolean>(false);

    const [comment, setComment] = useState("");
    // Кои обединени фактури са разгънати. Ключът е документът, а не редът: подредбата се мени при
    // сортиране и разгънатото не бива да скача на друга фактура.
    const [expandedDocumentIds, setExpandedDocumentIds] = useState<Set<number>>(new Set());

    const isSentTab = activeSubTab === 'sent';
    const currentHeaders = isSentTab ? sentInvoicesTableHeaders : invoicesTableHeaders;

    /**
     * Обединената фактура като един ред: редовете на един документ се събират, а отделните фактури
     * излизат под него със стрелката.
     *
     * Редът, който представя групата, е първият от нея - той носи документа, значи и общата сума. Броят
     * редове идва от `documentInvoiceIds`, тоест от документа, а не от страницата: филтърът по дата или
     * начин на плащане може да покаже само част от тях и тогава под стрелката се вижда по-малко, отколкото
     * казва номерът. За това се грижи етикетът „показани X от Y".
     */
    const groupedRows = useMemo(() => {
        const groups: { parent: Invoice; children: Invoice[]; documentId?: number; rowsInDocument: number }[] = [];
        const indexByDocument = new Map<number, number>();

        paidInvoices.forEach((invoice) => {
            const documentId = invoice.document?.id;
            const rowsInDocument = invoice.documentInvoiceIds?.length ?? 1;
            // Кредитното известие идва като втори ред със същия документ, но е отделен документ за окото -
            // затова групираме само същинските редове на фактурата.
            const groupable = documentId != null && rowsInDocument > 1 && !invoice.creditNoteDate;

            if (!groupable) {
                groups.push({ parent: invoice, children: [], rowsInDocument: 1 });
                return;
            }

            const existing = indexByDocument.get(documentId as number);
            if (existing == null) {
                indexByDocument.set(documentId as number, groups.length);
                groups.push({ parent: invoice, children: [invoice], documentId, rowsInDocument });
                return;
            }
            groups[existing].children.push(invoice);
        });

        return groups;
    }, [paidInvoices]);

    const toggleDocument = (documentId: number) =>
        setExpandedDocumentIds((prev) => {
            const next = new Set(prev);
            if (next.has(documentId)) next.delete(documentId);
            else next.add(documentId);
            return next;
        });

    // Платеното задължение се връща при останалите, дори да е било изпратено по мейл. Там бутонът праща
    // за първи път, а такова изпращане сървърът отказва - затова редът остава без него и без отметка за
    // групово изпращане. На подтаба с изпратените изпращането е повторно и е разрешено.
    const cannotBeEmailed = ({ state, document }: Invoice) =>
        document?.invoiceNumber == undefined
        || (!isSentTab && state === 'sent by mail');

    const useActionHook = async (fields: Invoice) => {
        if (fields.openingBalanceId) return;
        await deleteInvoices(fields, comment);
        refetchEntitiesHook();
        toast.success(<CustomToastContent content={[t('invoiceDeleted')]} />, toastOptionsTopRight);
    };

    /**
     * Отваря модален диалог за потвърждение с актуалните имейл адреси на клиента
     */
    const openEmailModal = async (fields: Invoice) => {
        setIdOfEmailLoadingInvoice(fields.id);
        setEmailTarget(fields);
        setEmailRecipients([]);
        setEmailRecipientStr(undefined);
        setLoadingRecipient(true);
        try {
            const { email, emails, recipients } = await getInvoiceEmailRecipient(fields.id ?? -1);
            setEmailRecipientStr(email || undefined);
            if (recipients && recipients.length > 0) {
                setEmailRecipients(recipients);
            } else if (emails && emails.length > 0) {
                setEmailRecipients(emails);
            } else if (email) {
                setEmailRecipients(email.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean));
            } else {
                setEmailRecipients([]);
            }
        } catch (error) {
            showBackendError(error);
        } finally {
            setLoadingRecipient(false);
            setIdOfEmailLoadingInvoice(undefined);
        }
    };

    const confirmSendEmail = async () => {
        if (!emailTarget) return;
        setEmailModalLoading(true);
        try {
            const id = isSentTab
                ? await resendInvoiceEmail(emailTarget.id ?? -1)
                : await emailInvoice(emailTarget.id ?? -1);
            if (id) toast.success(<CustomToastContent content={[t('emailSuccess')]} />, toastOptionsTopRight);
            refetchEntitiesHook();
            if (!isSentTab) {
                setSelectedItems(prevState => prevState.filter(({ id }) => id !== emailTarget.id));
            }
            setEmailTarget(undefined);
        } catch (error) {
            showBackendError(error);
            refetchEntitiesHook();
        } finally {
            setEmailModalLoading(false);
        }
    };

    const handleMultiSelectedSendEmail = async () => {
        setMultiMailLoading(true);
        try {
            const response = await emailInvoices(selectedItems.map(({ id }) => id ?? -1));
            if (response) {
                toast.success(<CustomToastContent content={[t('emailSuccess')]} />, toastOptionsTopRight);
                refetchEntitiesHook();
                setSelectedItems(() => []);
            }
        } catch (error) {
            showBackendError(error);
            refetchEntitiesHook();
        } finally {
            setMultiMailLoading(false);
        }
    };

    useEffect(() => {
        setSelectedItems(() => []);
    }, [refetchEntitiesHook]);

    /**
     * Редът на обединената фактура: един ред с общата сума на документа.
     *
     * Сумата идва от документа, а не от сбора на видимите редове - филтърът може да е скрил част от тях,
     * а сумата на фактурата не се мени от това какво е показано. Обектът е първият, а останалите са в
     * туултипа на иконката.
     */
    const renderSettlement = (invoice: Invoice, wholeDocument = false) => {
        const document = wholeDocument ? invoice.document : undefined;
        const total = document?.total ?? invoice.total;
        const currency = getCurrencySymbol(invoice.currency);
        const money = (amount: number) => `${transformPriceToString(amount)}${currency}`;
        if (['creditnote', 'storno'].includes(invoice.documentType?.toLowerCase())) return money(total);
        const paid = document ? document.paid : invoice.paid;
        const settled = paid ? total : (document ? document.paidAmount ?? 0 : invoice.paidAmount ?? 0);
        const remaining = Math.max(0, total - settled);
        return <div>
            <div>{money(total)}</div>
            <small className='d-block text-muted'>{openingT('settledAmount', { amount: money(settled) })}</small>
            <small className={`d-block ${remaining > 0 ? 'text-danger' : 'text-muted'}`}>
                {openingT('remainingAmount', { amount: money(remaining) })}
            </small>
        </div>;
    };

    const renderGroupRow = (group: { parent: Invoice; children: Invoice[]; documentId?: number; rowsInDocument: number }) => {
        const { parent, children, documentId, rowsInDocument } = group;
        const expanded = documentId != null && expandedDocumentIds.has(documentId);
        const rowNumber = parent.invoiceNumber ?? parent.document?.invoiceNumber;
        const hiddenRows = rowsInDocument - children.length;
        // Обединяват се и няколко периода на един и същи обект - тогава иконката няма какво да покаже.
        const otherBranches = Array.from(new Set(children.map(({ branchName }) => branchName)))
            .filter((name) => name !== parent.branchName);

        const numberCell = (
            <span className='d-inline-flex align-items-center'>
                {/* Мишената е спанът с подложката, не самата стрелка - иконката е няколко пиксела. Клик
                    върху клетката отваря фактурата, затова разгъването спира събитието. */}
                <span
                    role='button'
                    aria-label={t(expanded ? 'collapseMergedInvoice' : 'expandMergedInvoice')}
                    title={t(expanded ? 'collapseMergedInvoice' : 'expandMergedInvoice')}
                    className='d-inline-flex align-items-center justify-content-center px-2 py-1 me-1 cursor-pointer'
                    onClick={(event) => {
                        event.stopPropagation();
                        documentId != null && toggleDocument(documentId);
                    }}
                >
                    <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
                </span>
                <span>
                    {rowNumber}
                    <span className='text-muted ms-1'>
                        ({hiddenRows > 0
                            ? t('mergedShownOf', { shown: children.length, count: rowsInDocument })
                            : rowsInDocument})
                    </span>
                </span>
            </span>
        );

        const branchCell = (
            <span className='d-inline-flex align-items-center'>
                {parent.branchName}
                {otherBranches.length > 0 && (
                    <OverlayTrigger
                        placement='right'
                        overlay={
                            <Tooltip id={`merged-branches-${documentId}`}>
                                <div className='text-start'>
                                    {otherBranches.map((name, index) => (
                                        <div key={index}>{name}</div>
                                    ))}
                                    {hiddenRows > 0 && <div className='fst-italic'>{t('andMoreRows', { count: hiddenRows })}</div>}
                                </div>
                            </Tooltip>
                        }
                    >
                        {/* Спанът е нужен: OverlayTrigger подава ref на детето си, а FontAwesomeIcon не
                            го препраща - без него туултипът не се закача където трябва. */}
                        <span className='ms-2 px-1 color-main cursor-pointer d-inline-flex'>
                            <FontAwesomeIcon icon={faLayerGroup} />
                        </span>
                    </OverlayTrigger>
                )}
            </span>
        );

        const row = [
            numberCell,
            transformDateToString(parent.creationDate),
            t(parent.documentType),
            branchCell,
            parent.document?.clientName ?? parent.companyName,
            parent.document?.eik ?? parent.eik,
            transformDateToString(earliest(children, 'fromDate')),
            transformDateToString(latest(children, 'duePaymentDate')),
            parent.user,
            renderSettlement(parent, true),
            <PaymentMethodIcon paymentMethod={parent.paymentMethod} />,
            paymentStatusLabel(parent, t, '-'),
        ];
        if (isSentTab) {
            row.push(parent.emailSentDate ? transformDateToString(parent.emailSentDate, 'dd/mm/yyyy HH:MM') : '');
        }
        return row;
    };

    /**
     * Един подред: същите колони като на обикновен ред, за да се чете като фактура, а не като откъс.
     *
     * Разликите са две: номерът е без означението „(2)" - то е на общия ред - и обектът е отместен, за да
     * личи, че редът виси под него.
     */
    const renderChildRow = (invoice: Invoice) => {
        const cells = renderInvoiceRow(invoice);
        cells[0] = invoice.invoiceNumber ?? invoice.document?.invoiceNumber;
        cells[3] = <span className='ps-3'>{invoice.branchName}</span>;
        return cells;
    };

    const renderInvoiceRow = (invoice: Invoice) => {
        const {
            branchName,
            companyName,
            currency,
            eik,
            fromDate,
            duePaymentDate,
            creationDate,
            documentType,
            user,
            paymentMethod,
            total,
            document,
            documentInvoiceIds,
            emailSentDate
        } = invoice;

        // Обединената фактура има по един ред на обект или период, а филтърът по дата може да покаже
        // само част от тях - тогава сумата на видимия ред не е сумата на фактурата. Означението казва
        // колко реда има зад номера и кой от тях е този.
        const mergedIds = [...(documentInvoiceIds ?? [])].sort((a, b) => a - b);
        // Редът на кредитното известие носи своя номер в invoiceNumber; документът е общ за него и за
        // фактурата, така че четенето само от документа показваше един и същ номер на двата реда.
        const rowNumber = invoice.invoiceNumber ?? document?.invoiceNumber;
        const invoiceNumberCell = rowNumber != null && mergedIds.length > 1
            ? `${rowNumber} (${t('mergedPart', {
                index: mergedIds.indexOf(invoice.id as number) + 1,
                count: mergedIds.length,
            })})`
            : rowNumber;
        const row = [
            invoiceNumberCell,
            transformDateToString(creationDate),
            invoice.openingBalanceId ? openingT('turnoverSource') : t(documentType),
            documentType.toLowerCase() == "receipt" ? "" : branchName,
            documentType.toLowerCase() == "receipt" ? t("privatePerson") : document?.clientName ?? companyName,
            documentType.toLowerCase() == "receipt" ? "" : document?.eik ?? eik,
            fromDate ? transformDateToString(fromDate) : '',
            duePaymentDate ? transformDateToString(duePaymentDate) : '',
            user,
            renderSettlement(invoice),
            invoice.openingBalanceId ? '—' : <PaymentMethodIcon {...{ paymentMethod }} />,
            invoice.openingBalanceId && !invoice.paid
                ? openingT(invoice.paidAmount ? 'partiallySettled' : 'notSettled')
                : paymentStatusLabel(invoice, t, '-'),
        ];
        if (isSentTab) {
            row.push(emailSentDate ? transformDateToString(emailSentDate, 'dd/mm/yyyy HH:MM') : '');
        }
        return row;
    };

    return (
        <>
            {paidInvoices.length > 0 ? (
                <>
                    <Suspense fallback={<Spinner animation='border' />}>
                        <ExportInvoices pagination={pagination} filter={filter}
                            fileName={t('invoices') + "-" + dateFormat(new Date(), date)}
                            wscols={currentHeaders} />
                    </Suspense>
                    {!isSentTab && selectedItems.length > 0 && (multiMailLoading ? <Spinner animation={'border'} /> :
                        <CustomButton
                            className='w-30 mb-3'
                            onClick={handleMultiSelectedSendEmail}
                            title={t('emailInvoice')}
                        />
                    )}
                    <CustomTable
                        selectable={!isSentTab}
                        columns={{
                            headings: currentHeaders,
                            items: groupedRows.map((group) =>
                                group.children.length > 1 ? renderGroupRow(group) : renderInvoiceRow(group.parent)
                            ),
                        }}
                        childRows={(_, index) => {
                            const group = groupedRows[index];
                            return group && group.children.length > 1 ? group.children.map(renderChildRow) : undefined;
                        }}
                        isRowExpanded={(_, index) => {
                            const documentId = groupedRows[index]?.documentId;
                            return documentId != null && expandedDocumentIds.has(documentId);
                        }}
                        viewComponentObject={(invoice) => {
                            const item = paidInvoices.find((value) => value.id === invoice.id && value.documentType === invoice.documentType);
                            setSelectedItem(item);
                        }}
                        accessEditRoles={[Role.FINANCIAL, Role.COORDINATOR]}
                        accessActionRoles={[Role.FINANCIAL, Role.COORDINATOR]}
                        editComponent={
                            ({ itemToEdit, setShowModal }: EditProps<Invoice>) =>
                                itemToEdit?.openingBalanceId
                                ? <OpeningBalances balanceId={itemToEdit.openingBalanceId} onClose={() => setShowModal(false)} onChanged={refetchEntitiesHook} />
                                : <EditDocument itemToEdit={itemToEdit} setShowModal={setShowModal}
                                    invoices={paidInvoices}
                                    refetchEntitiesHook={refetchEntitiesHook} />
                        }
                        actionHook={useActionHook}
                        editUsesOwnDialog={row => !!row.openingBalanceId}
                        disableAction={row => !!row.openingBalanceId}
                        actionDisabledReason={row => row.openingBalanceId ? openingT('debtDeleteDisabled') : undefined}
                        comment={comment}
                        setComment={setComment}
                        sendEmailHook={openEmailModal}
                        tableData={groupedRows.map((group) => group.parent)}
                        {...{ refetchEntitiesHook }}
                        setSelectedItems={!isSentTab ? setSelectedItems : undefined}
                        notSelectable={paidInvoices.filter(cannotBeEmailed)}
                        disableEmail={cannotBeEmailed}
                        sortableColumns={SORTABLE_COLUMNS}
                        {...{ sortBy, sortDirection, onSortChange }}
                        rowClassName={invoiceRowClassName}
                        rowExtraAction={({ id, companyName, openingBalanceId }) => (
                            <BranchActionComponent
                                invoiceId={id}
                                openingBalanceId={openingBalanceId}
                                companyName={companyName}
                                accessEditRoles={[Role.COORDINATOR, Role.FINANCIAL]}
                            />
                        )}
                        idOfInvoiceEmailThatIsLoading={idOfEmailLoadingInvoice}
                    />
                    <PaginationComponent recordsCount={pager.totalCount} pagination={pagination} />
                    <ResendEmailModal
                        show={emailTarget !== undefined}
                        isResend={isSentTab}
                        invoiceLabel={`${t('invoice')} № ${emailTarget?.document?.invoiceNumber ?? ''} - ${emailTarget?.companyName ?? ''}`}
                        lastSentAt={emailTarget?.emailSentDate
                            ? transformDateToString(emailTarget.emailSentDate, 'dd/mm/yyyy HH:MM')
                            : undefined}
                        recipient={emailRecipientStr}
                        recipients={emailRecipients}
                        loading={emailModalLoading}
                        loadingRecipient={loadingRecipient}
                        onConfirm={confirmSendEmail}
                        onCancel={() => setEmailTarget(undefined)}
                    />
                </>
            ) : (
                <NoDataComponent noDataText={isSentTab ? commonT('sentEmails') : t('invoices')}></NoDataComponent>
            )}
        </>
    );
};

export const EditInvoiceWrapper = (props: EditProps<Invoice>) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoice' });
    const editForSaleInvoice = useEditForSaleInvoice;
    const editInvoices = useEditInvoices;

    const additionalProps = {
        useSubmit: async (formValue: EditInvoice) => {
            props.setIsLoading && props.setIsLoading(() => true);

            // „Запази" върши две различни неща: променя сумата и отстъпката, и сменя статуса. За
            // продажбите вторият случай има свой ендпойнт - редакционният път изобщо не носи статуса и
            // отказва вече издадена фактура, така че смяната на „платена" оттам не стигаше доникъде.
            const sale = props.itemToEdit;
            const marksSaleAsPaid = sale?.isSaleInvoice && !sale.paid && formValue.isPaid === InvoicePaymentStatus.Paid
                && sale.document?.invoiceNumber != null;

            const { id } = marksSaleAsPaid
                ? await markInvoiceAsPaid({
                    id: sale!.id as number,
                    invoiceNumber: sale!.document!.invoiceNumber,
                    isSale: true,
                }).then(() => ({ id: sale!.id }))
                : props.itemToEdit?.isSaleInvoice
                    ? await editForSaleInvoice(formValue)
                    : await editInvoices(formValue);

            resourceCache(useGetSales).clear();

            resourceCache(useGetInvoices).clear();
            props.refetchEntitiesHook();
            props.setShowModal(false);
            props.setIsLoading && props.setIsLoading(() => false);
            if (id) toast.success(<CustomToastContent content={[t('success')]} />, toastOptionsTopRight);
            resourceCache(fetchTasksAll).clear();
        },
    };

    return <AddEditInvoice {...props} {...additionalProps} />;
};
